import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AuthUser {
  email: string;
  name: string;
  role: string;
  token: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly AUTH_TOKEN_KEY = 'auth_token';
  private readonly AUTH_USER_KEY = 'auth_user';
  private readonly PENDING_VERIFICATION_KEY = 'pending_verification';
  private readonly VERIFICATION_CODE_KEY = 'verification_code';

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasValidToken());
  private currentUserSubject = new BehaviorSubject<AuthUser | null>(this.getStoredUser());

  public isAuthenticated$: Observable<boolean> = this.isAuthenticatedSubject.asObservable();
  public currentUser$: Observable<AuthUser | null> = this.currentUserSubject.asObservable();

  constructor(private router: Router) {
    // Check token validity on service initialization
    this.checkTokenValidity();
  }

  /**
   * Check if there's a valid token in storage
   */
  private hasValidToken(): boolean {
    const token = localStorage.getItem(this.AUTH_TOKEN_KEY);
    return !!token && token.length > 0;
  }

  /**
   * Get stored user from localStorage
   */
  private getStoredUser(): AuthUser | null {
    const userJson = localStorage.getItem(this.AUTH_USER_KEY);
    if (userJson) {
      try {
        return JSON.parse(userJson);
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Check if token is still valid (can be extended for JWT expiration check)
   */
  private checkTokenValidity(): void {
    const token = localStorage.getItem(this.AUTH_TOKEN_KEY);
    if (!token) {
      this.isAuthenticatedSubject.next(false);
      this.currentUserSubject.next(null);
    }
  }

  /**
   * Get current authentication status synchronously
   */
  get isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  /**
   * Get current user synchronously
   */
  get currentUser(): AuthUser | null {
    return this.currentUserSubject.value;
  }

  /**
   * Login with email and password
   * In a real application, this would make an API call
   * Now redirects to two-step verification instead of completing login
   */
  login(email: string, password: string, rememberMe: boolean = false): Observable<boolean> {
    return new Observable(observer => {
      // Simulate API call delay
      setTimeout(() => {
        // Demo validation - in production, this would be an API call
        if (email && password) {
          // Generate a mock token (in production, this comes from the server)
          const mockToken = btoa(`${email}:${Date.now()}`);

          const user: AuthUser = {
            email: email,
            name: this.extractNameFromEmail(email),
            role: 'admin',
            token: mockToken
          };

          // Store pending verification data (don't complete auth yet)
          const pendingData = {
            user: user,
            rememberMe: rememberMe,
            timestamp: Date.now()
          };
          localStorage.setItem(this.PENDING_VERIFICATION_KEY, JSON.stringify(pendingData));

          // Generate and store a mock verification code (in production, this would be sent via email/SMS)
          const verificationCode = this.generateVerificationCode();
          localStorage.setItem(this.VERIFICATION_CODE_KEY, verificationCode);

          // Log the code for demo purposes (remove in production)
          console.log('Verification code:', verificationCode);

          observer.next(true);
          observer.complete();
        } else {
          observer.next(false);
          observer.complete();
        }
      }, 1500);
    });
  }

  /**
   * Generate a 6-digit verification code
   * For demo purposes, returns fixed code "123456"
   */
  private generateVerificationCode(): string {
    return '123456';
  }

  /**
   * Check if there's a pending verification
   */
  hasPendingVerification(): boolean {
    const pendingData = localStorage.getItem(this.PENDING_VERIFICATION_KEY);
    if (!pendingData) return false;

    try {
      const data = JSON.parse(pendingData);
      // Check if verification request is not older than 10 minutes
      const isValid = Date.now() - data.timestamp < 10 * 60 * 1000;
      if (!isValid) {
        this.cancelVerification();
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get the email of the user pending verification
   */
  getPendingVerificationEmail(): string | null {
    const pendingData = localStorage.getItem(this.PENDING_VERIFICATION_KEY);
    if (!pendingData) return null;

    try {
      const data = JSON.parse(pendingData);
      return data.user?.email || null;
    } catch {
      return null;
    }
  }

  /**
   * Verify the 6-digit code
   */
  verifyCode(code: string): Observable<boolean> {
    return new Observable(observer => {
      setTimeout(() => {
        const storedCode = localStorage.getItem(this.VERIFICATION_CODE_KEY);
        const pendingData = localStorage.getItem(this.PENDING_VERIFICATION_KEY);

        if (code === storedCode && pendingData) {
          try {
            const data = JSON.parse(pendingData);
            const user = data.user as AuthUser;

            // Complete authentication
            localStorage.setItem(this.AUTH_TOKEN_KEY, user.token);
            localStorage.setItem(this.AUTH_USER_KEY, JSON.stringify(user));

            // Clean up verification data
            localStorage.removeItem(this.PENDING_VERIFICATION_KEY);
            localStorage.removeItem(this.VERIFICATION_CODE_KEY);

            // Update subjects
            this.isAuthenticatedSubject.next(true);
            this.currentUserSubject.next(user);

            observer.next(true);
          } catch {
            observer.next(false);
          }
        } else {
          observer.next(false);
        }
        observer.complete();
      }, 1000);
    });
  }

  /**
   * Resend verification code
   */
  resendVerificationCode(): Observable<boolean> {
    return new Observable(observer => {
      setTimeout(() => {
        if (this.hasPendingVerification()) {
          // Generate new code
          const newCode = this.generateVerificationCode();
          localStorage.setItem(this.VERIFICATION_CODE_KEY, newCode);

          // Log for demo purposes
          console.log('New verification code:', newCode);

          observer.next(true);
        } else {
          observer.next(false);
        }
        observer.complete();
      }, 500);
    });
  }

  /**
   * Cancel pending verification
   */
  cancelVerification(): void {
    localStorage.removeItem(this.PENDING_VERIFICATION_KEY);
    localStorage.removeItem(this.VERIFICATION_CODE_KEY);
  }

  /**
   * Logout the current user
   */
  logout(): void {
    // Clear stored data
    localStorage.removeItem(this.AUTH_TOKEN_KEY);
    localStorage.removeItem(this.AUTH_USER_KEY);

    // Update subjects
    this.isAuthenticatedSubject.next(false);
    this.currentUserSubject.next(null);

    // Navigate to login page
    this.router.navigate(['/login']);
  }

  /**
   * Get the authentication token for API calls
   */
  getToken(): string | null {
    return localStorage.getItem(this.AUTH_TOKEN_KEY);
  }

  /**
   * Extract a display name from email address
   */
  private extractNameFromEmail(email: string): string {
    const namePart = email.split('@')[0];
    // Convert john.stevens to John Stevens
    return namePart
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  /**
   * Get the URL to redirect to after login
   */
  getRedirectUrl(): string {
    return '/dashboard';
  }
}
